import {
	Box,
	Card,
	CardHeader,
	Dialog,
	DialogContent,
	DialogTitle,
	FormControl,
	IconButton,
	InputLabel,
	ListItemText,
	MenuItem,
	Select,
	TextField
} from '@material-ui/core'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import { AddOutlined, ArrowForwardIosOutlined, Close } from '@material-ui/icons'
import { Autocomplete } from '@material-ui/lab'
import { useFormik } from 'formik'
import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { NavLink, useHistory } from 'react-router-dom'
import * as yup from 'yup'

import { getMarkets } from '../../actions/markets/markets.actions'
import { showError, showSuccess } from '../../actions/misc/misc.actions'
import { MainButton, MainTitle } from '../../components'
import { api } from '../../services/api'
import { states } from '../../services/helpers'
import { TIME_ZONE_ABBREVIATIONS } from '../../utils'

const useStyles = makeStyles((theme) => ({
	form: {
		display: 'flex',
		alignItems: 'center',
		flexWrap: 'wrap',
		justifyContent: 'space-between',
		'& .MuiTextField-root, .MuiAutocomplete-root, .MuiFilledInput-root': {
			// width: 270,
			minWidth: 270
		}
	},
	input: {
		marginBottom: theme.spacing(2)
		// marginRight: theme.spacing(2)
	},
	container: {
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'flex-start',
		flexWrap: 'wrap',
		alignItems: 'flex-start',
		padding: 24
	},
	card: {
		width: 350,
		marginRight: 24
	},
	header: {
		textDecoration: 'none'
	},
	arrow: {
		marginTop: 6
	},
	dialogTitle: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		'& .MuiTypography-root': {
			width: '100%'
		}
	}
}))

const validationSchema = yup.object({
	city: yup.string().required(),
	state: yup.string().required(),
	time_zone: yup.string().oneOf(Object.keys(TIME_ZONE_ABBREVIATIONS)),
	phone_number: yup.string().required(),
	latitude: yup.number().required(),
	longitude: yup.number().required()
})

const Markets = () => {
	const markets = useSelector((state) => state.specific.markets.markets)

	const sorted = markets.sort((a, b) => {
		if (a.city < b.city) return -1
		if (a.city > b.city) return 1
		return 0
	})

	const [isAddNewMarketOpen, setIsAddNewMarketOpen] = useState(false)

	const dispatch = useDispatch()

	const classes = useStyles()
	const theme = useTheme()
	const history = useHistory()

	const formik = useFormik({
		initialValues: {
			city: '',
			state: '',
			time_zone: 'EST',
			phone_number: '',
			latitude: '',
			longitude: ''
		},
		validationSchema,
		validateOnMount: true,
		onSubmit: async (values) => {
			try {
				await api.post('/market', values)
				dispatch(getMarkets())
				setIsAddNewMarketOpen(false)
				dispatch(showSuccess('Market created'))
			} catch (error) {
				dispatch(showError(error))
			}
		}
	})

	const updateMarket = (key, val) => {
		formik.setFieldValue(key, val)
	}

	const onCloseDialog = () => {
		if (!formik.isSubmitting) setIsAddNewMarketOpen(false)
	}

	return (
		<div className={classes.container}>
			<MainTitle
				title="Markets"
				rightActions={[
					<MainButton
						label="New Market"
						onClick={() => setIsAddNewMarketOpen(true)}
						startIcon={<AddOutlined style={{ fill: theme.palette.secondary.main }} />}
					/>
				]}
			/>
			<Box display="flex" flexWrap="wrap">
				{markets && markets.length ? (
					sorted
						.filter((m) => !m.is_empty &&  m.active !== false)
						.map((m) => {
							return (
								<NavLink
									to={`/globalAdmin/markets/${m._id}/home`}
									style={{ textDecoration: 'none' }}
									onClick={() => dispatch({ type: 'SET_MARKET', payload: m })}>
									<Card className={classes.card}>
										<CardHeader
											title={`${m.city}, ${m.state}`}
											className={classes.header}
											action={
												<IconButton className={classes.arrow} aria-label="show more">
													<ArrowForwardIosOutlined
														style={{ fill: '#777679', fontSize: 18 }}
													/>
												</IconButton>
											}
										/>
									</Card>
								</NavLink>
							)
						})
				) : (
					<div>none here</div>
				)}
			</Box>
			<Dialog
				open={isAddNewMarketOpen}
				onClose={onCloseDialog}
				PaperProps={{
					style: {
						// width: 920,
						maxWidth: 920,
						// minHeight: 374,
						// maxHeight: 500,
						overflowY: 'scroll'
					}
				}}>
				<DialogTitle style={{ fontSize: 16 }} className={classes.dialogTitle}>
					<Box display="flex" alignItems="center">
						<Box flexGrow={1}>ADD NEW MARKET</Box>
						<Box>
							<IconButton onClick={onCloseDialog}>
								<Close color="inherit" fontSize="small" />
							</IconButton>
						</Box>
					</Box>
				</DialogTitle>
				<DialogContent>
					<Box paddingTop={2}>
						<form className={classes.form} onSubmit={formik.handleSubmit}>
							<TextField
								className={classes.input}
								value={formik.values.city}
								onChange={(e, v) => updateMarket('city', e.target.value)}
								variant="filled"
								label="City"
								required
							/>

							<Autocomplete
								size="small"
								getOptionLabel={(option) => option.name}
								id="household-select"
								className={classes.input}
								required
								options={states || []}
								value={
									formik.values.state
										? states.find((s) => s.abbreviation === formik.values.state)
										: ''
								}
								onChange={(e, v) => updateMarket('state', v?.abbreviation)}
								onClose={(e) => {
									e.target.value = ''
								}}
								renderOption={(option) => (
									<React.Fragment>
										<ListItemText primary={option.name} />
									</React.Fragment>
								)}
								renderInput={(params) => (
									<TextField {...params} variant="filled" label="State" required />
								)}
							/>

							<FormControl className={classes.input} required>
								<InputLabel>Timezone</InputLabel>
								<Select
									required
									variant="filled"
									onChange={(e) => {
										updateMarket('time_zone', e.target.value)
									}}>
									{Object.entries(TIME_ZONE_ABBREVIATIONS).map(([val, label]) => (
										<MenuItem key={val} value={val}>
											{label}
										</MenuItem>
									))}
								</Select>
							</FormControl>

							<TextField
								className={classes.input}
								value={formik.values.phone_number}
								variant="filled"
								required
								label="Phone Number"
								onChange={(e, v) => updateMarket('phone_number', e.target.value)}
							/>

							<TextField
								className={classes.input}
								value={formik.values.latitude}
								variant="filled"
								required
								type="number"
								label="Latitude"
								onChange={(e, v) => updateMarket('latitude', e.target.value)}
							/>

							<TextField
								className={classes.input}
								value={formik.values.longitude}
								variant="filled"
								required
								type="number"
								label="Longitude"
								onChange={(e, v) => updateMarket('longitude', e.target.value)}
							/>

							<Box width="100%" paddingTop={2}>
								<MainButton
									variant="contained"
									label="ADD MARKET"
									onClick={() => formik.submitForm()}
									disabled={!formik.isValid || formik.isSubmitting}
								/>
							</Box>
						</form>
					</Box>
				</DialogContent>
			</Dialog>
		</div>
	)
}

export default Markets
